import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { MoreHorizontal, Search, KeyRound, Ban, CheckCircle2, Eye, EyeOff, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function Students() {
    const [q, setQ] = useState("");
    const [rows, setRows] = useState([]);
    const [showIds, setShowIds] = useState(false);
    const [confirm, setConfirm] = useState(null); // {type, user}
    const [wipeOpen, setWipeOpen] = useState(false);
    const [wipeConfirmText, setWipeConfirmText] = useState("");
    const [wiping, setWiping] = useState(false);

    const doWipe = async () => {
        setWiping(true);
        try {
            const { data } = await api.delete("/admin/students/wipe");
            toast.success(`تم حذف ${data.deleted.students} طالبة، ${data.deleted.student_accounts} حساب طالبة، ${data.deleted.parent_accounts} حساب ولي أمر، ${data.deleted.classes} فصل`);
            setWipeOpen(false);
            setWipeConfirmText("");
            load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setWiping(false); }
    };

    const load = async () => {
        try {
            const { data } = await api.get("/admin/students", { params: q ? { q } : {} });
            setRows(data);
        } catch (e) { toast.error(formatApiError(e)); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

    const resetPw = async (userId) => {
        try {
            const { data } = await api.post(`/admin/users/${userId}/reset-password`, {});
            toast.success(`تمت الإعادة. كلمة المرور: ${data.new_password}`);
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const toggle = async (userId) => {
        try {
            const { data } = await api.post(`/admin/users/${userId}/toggle-active`);
            toast.success(data.active ? "تم تفعيل الحساب" : "تم إيقاف الحساب");
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const mask = (id) => showIds ? id : `${id.slice(0, 2)}${"•".repeat(Math.max(0, id.length - 4))}${id.slice(-2)}`;

    return (
        <div className="space-y-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h1 className="text-2xl font-bold text-[#0F172A]">حسابات الطالبات</h1>
                    <p className="text-sm text-slate-500 mt-1">{rows.length} طالبة</p>
                </div>
                <div className="flex items-center gap-2">
                    <div className="relative">
                        <Search className="w-4 h-4 text-slate-400 absolute right-3 top-1/2 -translate-y-1/2" />
                        <Input data-testid="table-search-input" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="بحث بالاسم أو رقم الهوية..." className="pr-10 w-80" />
                    </div>
                    <Button variant="outline" onClick={load} data-testid="btn-refresh">تحديث</Button>
                    <Button variant="ghost" size="icon" onClick={() => setShowIds((s) => !s)} data-testid="btn-toggle-ids" title="إظهار/إخفاء أرقام الهوية">
                        {showIds ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                    <Button
                        variant="outline"
                        onClick={() => setWipeOpen(true)}
                        data-testid="btn-wipe-students"
                        className="text-[#C8102E] border-[#FECACA] hover:bg-[#FFF1F2] hover:text-[#C8102E]"
                    >
                        <Trash2 className="w-4 h-4 ml-1" />
                        حذف الكل
                    </Button>
                </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="students-data-table">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
                            <tr>
                                <th className="px-4 py-3 text-right font-semibold">الاسم</th>
                                <th className="px-4 py-3 text-right font-semibold">الصف</th>
                                <th className="px-4 py-3 text-right font-semibold">الفصل</th>
                                <th className="px-4 py-3 text-right font-semibold">اسم المستخدم</th>
                                <th className="px-4 py-3 text-right font-semibold">رقم الهوية</th>
                                <th className="px-4 py-3 text-right font-semibold">الحالة</th>
                                <th className="px-4 py-3 text-right font-semibold">آخر دخول</th>
                                <th className="px-4 py-3 text-right font-semibold w-16"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.length === 0 && (
                                <tr><td colSpan={8} className="text-center py-12 text-slate-400">لا توجد بيانات — ابدئي بالاستيراد من صفحة "استيراد ملفات Excel"</td></tr>
                            )}
                            {rows.map((r) => (
                                <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50/60">
                                    <td className="px-4 py-3 font-medium text-[#0F172A]">{r.full_name}</td>
                                    <td className="px-4 py-3 text-slate-600">{r.grade}</td>
                                    <td className="px-4 py-3 text-slate-600">{r.section}</td>
                                    <td className="px-4 py-3 font-mono-num text-slate-700" dir="ltr">{r.username}</td>
                                    <td className="px-4 py-3 font-mono-num text-slate-500" dir="ltr">{mask(r.national_id)}</td>
                                    <td className="px-4 py-3">
                                        {r.user_active
                                            ? <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">نشط</Badge>
                                            : <Badge className="bg-red-100 text-red-800 hover:bg-red-100">موقوف</Badge>}
                                        {r.must_change_password && <Badge variant="outline" className="mr-1 border-amber-300 text-amber-700 text-[10px]">أول دخول</Badge>}
                                    </td>
                                    <td className="px-4 py-3 text-xs text-slate-500 font-mono-num" dir="ltr">{r.last_login_at ? new Date(r.last_login_at).toLocaleString("ar-EG") : "—"}</td>
                                    <td className="px-4 py-3">
                                        <DropdownMenu>
                                            <DropdownMenuTrigger asChild>
                                                <Button variant="ghost" size="icon" data-testid={`row-menu-${r.id}`}><MoreHorizontal className="w-4 h-4" /></Button>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent align="start">
                                                <DropdownMenuItem onClick={() => setConfirm({ type: "reset", userId: r.user_id, name: r.full_name })} data-testid="action-reset-password-button">
                                                    <KeyRound className="w-4 h-4 ml-2" /> إعادة تعيين كلمة المرور
                                                </DropdownMenuItem>
                                                <DropdownMenuSeparator />
                                                <DropdownMenuItem onClick={() => setConfirm({ type: "toggle", userId: r.user_id, name: r.full_name, active: r.user_active })} data-testid="action-disable-account-button">
                                                    {r.user_active ? <><Ban className="w-4 h-4 ml-2" /> إيقاف الحساب</> : <><CheckCircle2 className="w-4 h-4 ml-2" /> تفعيل الحساب</>}
                                                </DropdownMenuItem>
                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
                <AlertDialogContent dir="rtl">
                    <AlertDialogHeader>
                        <AlertDialogTitle>{confirm?.type === "reset" ? "إعادة تعيين كلمة المرور" : (confirm?.active ? "إيقاف الحساب" : "تفعيل الحساب")}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {confirm?.type === "reset"
                                ? `سيتم إعادة كلمة مرور ${confirm?.name} إلى رقم الهوية، وسيُطلب منها تغييرها عند الدخول.`
                                : `هل تريدين ${confirm?.active ? "إيقاف" : "تفعيل"} حساب ${confirm?.name}؟`}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>إلغاء</AlertDialogCancel>
                        <AlertDialogAction onClick={async () => {
                            if (confirm.type === "reset") await resetPw(confirm.userId);
                            else await toggle(confirm.userId);
                            setConfirm(null);
                        }} className="bg-[#1B2A4E] hover:bg-[#0F172A]">تأكيد</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <AlertDialog open={wipeOpen} onOpenChange={(o) => { if (!o) { setWipeOpen(false); setWipeConfirmText(""); } }}>
                <AlertDialogContent dir="rtl">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="text-[#C8102E]">حذف جميع بيانات الطالبات</AlertDialogTitle>
                        <AlertDialogDescription className="space-y-2">
                            <div>
                                سيتم حذف <span className="font-bold text-[#0F172A]">{rows.length}</span> طالبة وجميع حسابات الطالبات وأولياء الأمور والفصول المرتبطة. هذا الإجراء لا يمكن التراجع عنه.
                            </div>
                            <div className="text-xs bg-amber-50 border border-amber-200 text-amber-900 rounded-lg p-3">
                                حساب الإدارة والمدربات لن يتأثروا. اكتبي كلمة <span className="font-bold">حذف</span> للتأكيد.
                            </div>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <Input
                        value={wipeConfirmText}
                        onChange={(e) => setWipeConfirmText(e.target.value)}
                        placeholder="اكتبي: حذف"
                        data-testid="wipe-confirm-input"
                        className="mt-2"
                    />
                    <AlertDialogFooter>
                        <AlertDialogCancel>إلغاء</AlertDialogCancel>
                        <Button
                            onClick={doWipe}
                            disabled={wipeConfirmText.trim() !== "حذف" || wiping}
                            data-testid="btn-confirm-wipe"
                            className="bg-[#C8102E] hover:bg-[#9F1239] text-white"
                        >
                            {wiping ? "جارٍ الحذف..." : "تأكيد الحذف"}
                        </Button>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
